import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { useAppSelector } from '@/store';
import { selectLeaseIds, selectAllLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetAutoPayStatusQuery, useCancelAutoPayMutation, useGetLeaseQuery, useGetPaymentMethodsQuery } from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import { getPaymentMethodShortLabel } from '@/utils/residents/paymentUtils';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { AutopayCard } from '@/components/residents/redesign/payments';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { SectionHeader } from '@/components/residents/redesign/SectionHeader';
import { GlassCard } from '@/components/residents/redesign/GlassCard';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { getDisplayName, getInitials } from '@/utils/residents/utils';

/**
 * Mobile Autopay Section (Account Settings)
 * Uses the unified redesign AutopayCard components.
 */
export const MobileAutopaySection: React.FC = () => {
  const { colors } = useThemeColors();
  const { showSuccess, showError } = useToast();
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [setupModalLeaseId, setSetupModalLeaseId] = useState<number | null>(null);
  const [editModalLeaseId, setEditModalLeaseId] = useState<number | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{ leaseId: number; autopayId: number } | null>(null);

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectAllLeaseIds);
  const connections = useAppSelector(linkedAcctsConnections);
  const connectionsFetchLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const authUser = useAppSelector((s) => (s as any).auth?.user);

  const allLeaseIds = useMemo(() => {
    const combined = [...ownLeaseIds];
    connectionLeaseIds.forEach((id) => {
      if (!combined.includes(id)) combined.push(id);
    });
    return combined;
  }, [ownLeaseIds, connectionLeaseIds]);

  const connectionUserByLeaseId = useMemo(() => {
    const map = new Map<number, any>();
    (connections || []).forEach((conn: any) => {
      (conn?.leaseIds || []).forEach((id: number) => map.set(id, conn.user));
    });
    return map;
  }, [connections]);

  const { refetch: autoPayStatusRefetch, isFetching } = useGetAutoPayStatusQuery();
  const { data: methodsData } = useGetPaymentMethodsQuery();
  const paymentMethods: any[] = useMemo(() => methodsData?.response || [], [methodsData]);

  const [cancelAutopay] = useCancelAutoPayMutation();

  const handleRefresh = useCallback(() => {
    autoPayStatusRefetch();
    setRefreshNonce((n) => n + 1);
  }, [autoPayStatusRefetch]);

  useFocusEffect(
    useCallback(() => {
      handleRefresh();
      return () => {};
    }, [handleRefresh])
  );

  const handleRemoveAutopay = useCallback((_leaseId: number, autopayId: number) => {
    setRemoveConfirm({ leaseId: _leaseId, autopayId });
  }, []);

  const handleRemoveConfirmed = useCallback(async () => {
    if (!removeConfirm?.autopayId) return;
    setRemoveConfirm(null);
    try {
      await cancelAutopay(removeConfirm.autopayId).unwrap();
      showSuccess(strings.account.autopayRemovedToast);
      handleRefresh();
    } catch (error: any) {
      showError(error?.data?.message || error?.data?.error?.message || 'Failed to remove autopay');
    }
  }, [removeConfirm, cancelAutopay, showSuccess, showError, handleRefresh]);

  const hasNoLeases = allLeaseIds.length === 0 && !connectionsFetchLoading;
  const isLoading = connectionsFetchLoading || isFetching;

  if (isLoading) {
    return (
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={PRIMITIVE_COLORS.resioBlue} />
        </View>
      </View>
    );
  }

  if (hasNoLeases) {
    return (
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <GlassCard style={styles.emptyCard}>
          <View style={styles.emptyIconOuter}>
            <View style={styles.emptyIconGlow} />
            <View style={styles.emptyIconContainer}>
              {RESIDENTS_SVG_COMPONENTS.PAY && (
                <RESIDENTS_SVG_COMPONENTS.PAY width={24} height={24} color={colors.accent.green} />
              )}
            </View>
          </View>
          <Text style={[styles.emptyTitle, { color: colors.text.primary }]}>{strings.account.autopayEmptyTitle}</Text>
          <Text style={[styles.emptyMessage, { color: colors.text.muted }]}>{strings.account.autopayEmptyMessage}</Text>
        </GlassCard>
      </View>
    );
  }

  return (
    <>
      <View style={styles.container}>
        <SectionHeader title="Autopay" />
        <View style={styles.leasesList}>
          {allLeaseIds.map((leaseId) => (
            <LeaseAutopayItemCard
              key={leaseId}
              leaseId={leaseId}
              isOwnLease={ownLeaseIds.includes(leaseId)}
              connectionUser={connectionUserByLeaseId.get(leaseId)}
              authUser={authUser}
              paymentMethods={paymentMethods}
              refreshNonce={refreshNonce}
              onSetup={() => setSetupModalLeaseId(leaseId)}
              onEdit={() => setEditModalLeaseId(leaseId)}
              onRemove={handleRemoveAutopay}
            />
          ))}
        </View>
      </View>

      {/* Remove Autopay Confirmation */}
      <ConfirmationModal
        visible={removeConfirm !== null}
        message="Are you sure you want to remove autopay? You will need to set it up again if you change your mind."
        onCancel={() => setRemoveConfirm(null)}
        onProceed={handleRemoveConfirmed}
        cancelText="No, cancel"
        proceedText="Yes, remove"
      />

      {/* Autopay Setup Modal */}
      {setupModalLeaseId !== null && (
        <MobileAutopayDetailsModal
          visible
          onClose={() => setSetupModalLeaseId(null)}
          leaseId={setupModalLeaseId}
          onAutopayUpdated={handleRefresh}
          mode="setup"
        />
      )}

      {/* Autopay Edit/View Modal */}
      {editModalLeaseId !== null && (
        <MobileAutopayDetailsModal
          visible
          onClose={() => setEditModalLeaseId(null)}
          leaseId={editModalLeaseId}
          onAutopayUpdated={handleRefresh}
          mode="view"
        />
      )}
    </>
  );
};

interface LeaseAutopayItemCardProps {
  leaseId: number;
  isOwnLease: boolean;
  connectionUser?: any;
  authUser?: any;
  paymentMethods: any[];
  refreshNonce: number;
  onSetup: () => void;
  onEdit: () => void;
  onRemove: (leaseId: number, autopayId: number) => void;
}

const LeaseAutopayItemCard: React.FC<LeaseAutopayItemCardProps> = React.memo(({
  leaseId,
  isOwnLease,
  connectionUser,
  authUser,
  paymentMethods,
  refreshNonce,
  onSetup,
  onEdit,
  onRemove,
}) => {
  const { data: leaseResp, refetch } = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId }
  );

  React.useEffect(() => {
    refetch();
  }, [refreshNonce, refetch]);

  const lease = (leaseResp as any)?.response?.lease;

  const propertyName = lease?.property?.marketingName || '—';
  const users = lease?.users || [];
  const primaryUser = users?.find((u: any) => u?.LeaseCustomer?.customerType === 'primary');
  const displayUser = isOwnLease
    ? (users?.find((u: any) => u?.id === authUser?.id) || primaryUser || users?.[0])
    : (connectionUser || primaryUser || users?.[0]);

  const firstName = displayUser?.firstName;
  const lastName = displayUser?.lastName;
  const photoUrl = displayUser?.photoUrl;
  const displayName = getDisplayName(firstName, lastName) || 'Resident';
  const initials = getInitials(firstName, lastName) || 'R';

  const recurringPayments = lease?.recurringPayments || [];
  const autopay = recurringPayments[0];
  const hasAutopay = Boolean(autopay);

  const paymentMethodLabel = useMemo(() => {
    if (!autopay?.sourceId) return '—';
    const method = paymentMethods.find((m: any) => {
      const methodSourceId = Number(m.sourceId) || m.id;
      return methodSourceId === Number(autopay.sourceId);
    });
    return method ? getPaymentMethodShortLabel(method) : '—';
  }, [autopay?.sourceId, paymentMethods]);

  const isSetupDisabled = useMemo(() => {
    const endDate = lease?.endDate ? new Date(lease.endDate) : null;
    if (!endDate || isNaN(endDate.getTime())) return false;
    const firstOfEndMonth = new Date(endDate);
    firstOfEndMonth.setDate(1);
    return firstOfEndMonth < new Date();
  }, [lease?.endDate]);

  if (!lease) return null;

  const formatDate = (iso?: string): string => {
    if (!iso) return 'End of lease';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '—';
    }
  };

  const amountText = autopay?.amount
    ? `$${Number(autopay.amount).toFixed(2)}`
    : (autopay?.payFullAmount !== false ? 'Full balance' : '—');

  return (
    <AutopayCard
      displayName={displayName}
      initials={initials}
      photoUrl={photoUrl}
      propertyName={propertyName}
      autopayEnabled={hasAutopay}
      amount={hasAutopay ? amountText : undefined}
      paymentMethodLabel={hasAutopay ? paymentMethodLabel : undefined}
      startDate={hasAutopay ? formatDate(autopay?.startDate || autopay?.createdAt) : undefined}
      endDate={hasAutopay ? formatDate(autopay?.endDate) : undefined}
      onSetup={onSetup}
      onEdit={onEdit}
      onRemove={hasAutopay ? () => onRemove(leaseId, autopay.id) : undefined}
      isSetupDisabled={isSetupDisabled}
    />
  );
});

LeaseAutopayItemCard.displayName = 'LeaseAutopayItemCard';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingTop: 8,
  },
  leasesList: {
    gap: 12,
  },
  loadingContainer: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  emptyCard: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyIconOuter: {
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  emptyIconGlow: {
    position: 'absolute',
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: 'rgba(102,206,102,0.06)',
  },
  emptyIconContainer: {
    width: 52,
    height: 52,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    marginBottom: 8,
    textAlign: 'center',
  },
  emptyMessage: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 280,
  },
});

export default MobileAutopaySection;
